import { useState, useEffect, useRef, useCallback } from 'react';
import { ChevronDown, Search, Loader2, X } from 'lucide-react';

export interface SearchOption {
  id: string;
  label: string;
  sublabel?: string;
}

interface Props {
  value: string;
  valueLabel?: string; // label of the currently-selected value (for display after load/edit)
  onChange: (id: string, option?: SearchOption) => void;
  fetchOptions: (search: string) => Promise<SearchOption[]>;
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  className?: string;
}

// A dependency-free async combobox: type to search the server (no client-side
// cap), pick a result. Used for accounts/contacts dropdowns that exceed 1000 rows.
export default function SearchableSelect({
  value, valueLabel, onChange, fetchOptions,
  placeholder = 'Select…', disabled, allowClear = true, className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<SearchOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [display, setDisplay] = useState(valueLabel || '');
  const boxRef = useRef<HTMLDivElement>(null);
  const debRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => { setDisplay(valueLabel || ''); }, [valueLabel]);
  useEffect(() => { if (!value) setDisplay(''); }, [value]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const runSearch = useCallback((q: string) => {
    setLoading(true);
    fetchOptions(q).then(setOptions).catch(() => setOptions([])).finally(() => setLoading(false));
  }, [fetchOptions]);

  useEffect(() => {
    if (open) runSearch(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const onType = (v: string) => {
    setQuery(v);
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => runSearch(v), 250);
  };

  const pick = (o: SearchOption) => {
    onChange(o.id, o);
    setDisplay(o.label);
    setOpen(false);
    setQuery('');
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setDisplay('');
    setQuery('');
  };

  return (
    <div ref={boxRef} className={`relative ${className || ''}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-input rounded-md bg-background text-sm text-left disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40"
      >
        <span className={`truncate ${display ? '' : 'text-muted-foreground'}`}>{display || placeholder}</span>
        <div className="flex items-center gap-1 shrink-0">
          {allowClear && display && !disabled && (
            <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" onClick={clear} />
          )}
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </div>
      </button>

      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full bg-background border border-border rounded-md shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="flex items-center gap-2 px-2 py-1.5 border-b border-border">
            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <input
              autoFocus
              value={query}
              onChange={e => onType(e.target.value)}
              placeholder="Type to search…"
              className="w-full bg-transparent text-sm focus:outline-none"
            />
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
          </div>
          <div className="overflow-y-auto">
            {options.length === 0 && !loading && (
              <div className="px-3 py-4 text-xs text-muted-foreground text-center">
                {query ? 'No matches' : 'Type to search'}
              </div>
            )}
            {options.map(o => (
              <button
                type="button"
                key={o.id}
                onClick={() => pick(o)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex flex-col"
              >
                <span className="truncate">{o.label}</span>
                {o.sublabel && <span className="text-xs text-muted-foreground truncate">{o.sublabel}</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
